import Form from "common/Form/Form";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Input from "common/Inputs/Input";
import { SecondaryButton } from "features/configuration/common/styles";
import styled, { useTheme } from "styled-components";
import { ReactNode, useCallback, useState } from "react";
import NewLookClientForm, {
  InlineFormError,
} from "features/portfolio/CreateAccount/NewLook/NewLookClientForm";
import QuickConnectForm from "features/configuration/accounts/CreateAccount/QuickConnectForm";
import SubmitButton from "features/onboarding/common/SubmitButton";
import useClientForm from "features/configuration/accounts/CreateAccount/useClientForm";
import { ClientFormProps } from "features/configuration/common/types";
import SelectSubExchange from "features/portfolio/CreateAccount/SelectSubExchange";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import { DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS } from "@shared/features/portfolio/constants";
import { validateDemoAccountInitialFunds } from "@shared/features/configuration/utils";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import {
  CREATE_ACCOUNT_API_KEY_ID,
  CREATE_ACCOUNT_API_PASSPHRASE_ID,
  CREATE_ACCOUNT_API_SECRET_ID,
  CREATE_ACCOUNT_CONNECT_EXCHANGE_BUTTON_ID,
} from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import DemoFeeFields from "features/configuration/accounts/CreateAccount/DemoFeeFields";

import { LanguageNameSpaces } from "@shared/types";

interface ClientFormWithFooterProps extends ClientFormProps {
  footer?: ReactNode;
}

const ClientForm = ({
  account,
  primaryExchange,
  onSubmitSuccess,
  closeDrawer,
  form,
  isOnboarding,
  getErrorNotification,
  isQuickConnect,
  getSuccessMessage,
  footer,
}: ClientFormWithFooterProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isNewLook } = useTheme();
  const [formError, setFormError] = useState<InlineFormError>();
  const onInlineError = useCallback(
    (description: string, title?: string) =>
      setFormError({ description, title }),
    []
  );
  const clearFormError = useCallback(() => setFormError(undefined), []);
  const clientForm = useClientForm({
    primaryExchange,
    account,
    onSubmitSuccess,
    closeDrawer,
    form,
    isOnboarding,
    getErrorNotification,
    getSuccessMessage,
    onInlineError: isNewLook ? onInlineError : undefined,
  });
  const {
    isMobile,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    validateApiInfo,
    isLoading,
    openConfirmModal,
    confirmModalContextHolder,
    accountSubExchange,
    isHyperLiquid,
    quickConnectLoading,
  } = clientForm;

  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  if (isNewLook) {
    return (
      <NewLookClientForm
        account={account}
        primaryExchange={primaryExchange}
        form={form}
        isOnboarding={isOnboarding}
        isQuickConnect={isQuickConnect}
        clientForm={clientForm}
        formError={formError}
        clearFormError={clearFormError}
        footer={footer}
      />
    );
  }

  const buttons = (
    <>
      <StyledSubmitButton
        onClick={form.submit}
        loading={mainButtonData.loading}
        disabled={mainButtonData.disabled}
        $isMobile={isMobile}
        data-testid={CREATE_ACCOUNT_CONNECT_EXCHANGE_BUTTON_ID}
      >
        {mainButtonData.text}
      </StyledSubmitButton>
      {secondaryButtonData.text && (
        <SecondaryButton
          onClick={secondaryButtonData.onClick}
          style={{ marginTop: 16 }}
        >
          {secondaryButtonData.text}
        </SecondaryButton>
      )}
    </>
  );

  const manualConnectFormItems = (
    <>
      {isDemoWorkspace && !isEdit && (
        <Form.Item
          label={t("create_account.initial_funds")}
          name="demo_initial_funds"
          initialValue={DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS}
          rules={[
            () => ({
              validator(_, value) {
                const error = validateDemoAccountInitialFunds(value);
                if (error) return Promise.reject(error);
                else return Promise.resolve();
              },
            }),
          ]}
        >
          <CustomInputNumber />
        </Form.Item>
      )}
      {isDemoWorkspace && <DemoFeeFields account={account} />}
      {!isDemoWorkspace && (
        <>
          <Form.Item
            label={
              isHyperLiquid
                ? t("create_account.account_wallet_address")
                : t("create_account.api_key")
            }
            name="api_key"
            rules={[
              () => ({
                validator() {
                  return validateApiInfo(
                    "api_key",
                    t("create_account.api_key")
                  );
                },
              }),
            ]}
          >
            <Input
              placeholder={account ? account.api_key : ""}
              data-testid={CREATE_ACCOUNT_API_KEY_ID}
            />
          </Form.Item>
          <Form.Item
            label={
              isHyperLiquid
                ? t("create_account.api_private_key")
                : t("create_account.api_secret")
            }
            name="api_secret"
            rules={[
              () => ({
                validator() {
                  return validateApiInfo(
                    "api_secret",
                    t("create_account.api_secret")
                  );
                },
              }),
            ]}
          >
            <Input
              placeholder={account ? "*********" : ""}
              data-testid={CREATE_ACCOUNT_API_SECRET_ID}
            />
          </Form.Item>
          {showPassphrase && (
            <Form.Item
              label={t("create_account.api_passphrase")}
              name="api_passphrase"
              rules={[
                () => ({
                  validator() {
                    return validateApiInfo(
                      "api_passphrase",
                      t("create_account.api_passphrase")
                    );
                  },
                }),
              ]}
            >
              <Input
                placeholder={account ? "*********" : ""}
                data-testid={CREATE_ACCOUNT_API_PASSPHRASE_ID}
              />
            </Form.Item>
          )}
          {whitelistIPs}
        </>
      )}
      {buttons}
      {isMobile && !isOnboarding && (
        <ConnectExchangeGuide
          primaryExchange={primaryExchange}
          selectedSubExchange={accountSubExchange}
          isQuickConnect={isQuickConnect}
        />
      )}
    </>
  );

  const editAccountNameMessage = isEdit && !isDemoWorkspace && (
    <Text style={{ marginTop: 16 }}>
      {t("create_account.edit_name_message")}
    </Text>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading && !quickConnectLoading}
      hideContentWhileLoading={true}
      wrapStyle={
        isBottomNavigator
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <Form
        form={form}
        name="clientForm"
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
        style={{ width: "100%" }}
      >
        <SelectSubExchange
          primaryExchange={primaryExchange}
          account={account}
          isEdit={isEdit}
          isLoading={isLoading}
        />
        {(!isQuickConnect || isDemoWorkspace) && manualConnectFormItems}
      </Form>
      {editAccountNameMessage}
      {isQuickConnect && (
        <QuickConnectForm
          primaryExchange={primaryExchange}
          openConfirmModal={openConfirmModal}
        />
      )}
      {quotaModal}
      {confirmModalContextHolder}
    </LoadingSpinnerOverlay>
  );
};

export default ClientForm;

const StyledSubmitButton = styled(SubmitButton)<{ $isMobile: boolean }>`
  margin: ${(p) => (p.$isMobile ? 0 : "12px 0 0 0")};
`;
